import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Bell, LogOut } from "lucide-react";
import Messages from "../components/Messages";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSeo } from "../lib/seo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PropertyCard, StatusBadge } from "../components/PropertyCard";
import CriteriaForm from "../components/CriteriaForm";
import { Disclaimer } from "../components/Common";

const Stat = ({ label, value, testid }) => (
  <div className="bg-white p-6" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-[0.14em] text-mist">{label}</p>
    <p className="metric mt-2 text-3xl">{value}</p>
  </div>
);

const Grid = ({ items, match, empty }) =>
  items.length ? (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{items.map((p) => <PropertyCard key={p.id} p={p} match={match ? p.match : null} />)}</div>
  ) : (
    <div className="border border-dashed border-navy/20 p-10 text-center text-mist">{empty}</div>
  );

function CriteriaSummary({ c }) {
  const rows = [["Property types", c.property_types?.join(", ")], ["Markets", [...(c.locations || []), c.location_details].filter(Boolean).join(", ")],
    ["Price range", c.min_price || c.max_price ? `$${Number(c.min_price || 0).toLocaleString()} – ${c.max_price ? "$" + Number(c.max_price).toLocaleString() : "open"}` : null],
    ["Capital available", c.available_capital && `$${Number(c.available_capital).toLocaleString()}`], ["Strategy", c.strategies?.join(", ")],
    ["Return requirements", [c.desired_cap_rate && `${c.desired_cap_rate}% cap`, c.desired_coc && `${c.desired_coc}% CoC`, c.desired_roi && `${c.desired_roi}% ROI`].filter(Boolean).join(" · ")],
    ["Custom requirements", c.notes]];
  return (
    <dl className="grid gap-px bg-navy/10 border border-navy/10 sm:grid-cols-2" data-testid="criteria-summary">
      {rows.map(([k, v]) => <div key={k} className="bg-white p-5"><dt className="text-[10px] uppercase tracking-wider text-mist">{k}</dt><dd className="mt-1.5 text-navy">{v || "—"}</dd></div>)}
    </dl>
  );
}

export default function Dashboard() {
  useSeo("Investor Portal");
  const { user, logout } = useAuth();
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(false);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "matches");
  useEffect(() => { if (params.get("tab")) setTab(params.get("tab")); }, [params]);
  const load = () => api.get("/me/dashboard").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);

  if (!d) return <div className="pt-40 container-x text-mist">Loading…</div>;
  const unread = d.notifications.filter((n) => !n.read).length;

  return (
    <section className="bg-paper pt-[110px] pb-24" data-testid="investor-dashboard">
      <div className="container-x">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Investor Portal</p>
            <h1 className="mt-3 text-4xl sm:text-5xl font-medium text-navy">Welcome, {user.name?.split(" ")[0]}.</h1>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-mist hover:text-navy" data-testid="dashboard-logout"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10 border border-navy/10">
          <Stat label="Matched Opportunities" value={d.matches.length} testid="stat-matches" />
          <Stat label="Active Opportunities" value={d.active_count} testid="stat-active" />
          <Stat label="Saved" value={d.saved.length} testid="stat-saved" />
          <Stat label="Requests" value={d.requests.length} testid="stat-requests" />
        </div>
        <Tabs value={tab} onValueChange={setTab} className="mt-10">
          <TabsList className="h-auto flex-wrap justify-start gap-1 bg-transparent p-0 border-b border-navy/10 w-full rounded-none">
            {[["matches", "Matched"], ["criteria", "My Criteria"], ["saved", "Saved"], ["requests", "Requests"], ["messages", "Messages"], ["recent", "Recently Added"], ["notifications", `Notifications${unread ? ` (${unread})` : ""}`]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} className="rounded-none px-4 py-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gold text-navy" data-testid={`dash-tab-${v}`}
                onClick={v === "notifications" && unread ? () => api.post("/me/notifications/read").then(load) : undefined}>{l}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="matches" className="mt-8 space-y-6">
            {!d.criteria && <div className="bg-navy text-paper p-6 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center"><p>Add your investment criteria to receive match scores.</p><Link to="/investors#criteria" className="btn-gold">Add Criteria</Link></div>}
            <Grid items={d.matches} match empty="No matches yet. We're searching Oregon for opportunities that fit your criteria." />
            <p className="text-xs text-mist">Match scores compare listed property characteristics with your stated criteria. They are not investment recommendations.</p>
          </TabsContent>
          <TabsContent value="criteria" className="mt-8">
            {editing || !d.criteria ? (
              <CriteriaForm mode="edit" initial={d.criteria || {}} onDone={() => { setEditing(false); load(); }} />
            ) : (
              <div className="space-y-4">
                <CriteriaSummary c={d.criteria} />
                <button onClick={() => setEditing(true)} className="btn-primary" data-testid="criteria-edit-btn">Edit Criteria</button>
              </div>
            )}
          </TabsContent>
          <TabsContent value="saved" className="mt-8"><Grid items={d.saved} empty="Save properties to keep them here." /></TabsContent>
          <TabsContent value="requests" className="mt-8">
            <div className="border border-navy/10 bg-white divide-y divide-navy/10" data-testid="requests-list">
              {d.requests.length === 0 && <p className="p-6 text-mist">You haven't requested information yet.</p>}
              {d.requests.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-4 p-5">
                  <div><p className="font-semibold text-navy">{r.property_title}</p><p className="text-xs text-mist">{r.property_city} · {new Date(r.created_at).toLocaleDateString()}</p></div>
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-mist">Approved requests unlock confidential details and documents on the property page.</p>
          </TabsContent>
          <TabsContent value="messages" className="mt-8"><Messages /></TabsContent>
          <TabsContent value="recent" className="mt-8"><Grid items={d.recent} empty="No recent opportunities." /></TabsContent>
          <TabsContent value="notifications" className="mt-8">
            <div className="border border-navy/10 bg-white divide-y divide-navy/10" data-testid="notifications-list">
              {d.notifications.length === 0 && <p className="p-6 text-mist">No notifications yet.</p>}
              {d.notifications.map((n) => (
                <div key={n.id} className="flex gap-4 p-5">
                  <Bell className={`h-4 w-4 mt-1 shrink-0 ${n.read ? "text-mist" : "text-gold"}`} />
                  <div className="flex-1"><p className="font-semibold text-navy">{n.title}</p><p className="text-sm text-ink/70">{n.body}</p>{n.link && <Link to={n.link} className="text-sm text-navy underline">View</Link>}</div>
                  <span className="text-xs text-mist">{new Date(n.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
        <div className="mt-12"><Disclaimer testid="dashboard-disclaimer" /></div>
      </div>
    </section>
  );
}
